import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardActionArea,
  Checkbox,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Stack,
} from '@mui/material';
import {
  Close,
  Add,
  Save,
  PhotoCamera,
  ArrowBack,
  CheckCircle,
} from '@mui/icons-material';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import firebase from 'firebase/app';

import { ENDPOINTS } from '../../config';

const PROXY_BASE = ENDPOINTS.getVenuePhoto;
const MAX_IMAGES = 5;
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

// Produce a cropped JPEG blob from a loaded <img> element and a pixel-crop spec
function getCroppedBlob(image, pixelCrop) {
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  ctx.drawImage(
    image,
    pixelCrop.x * scaleX,
    pixelCrop.y * scaleY,
    pixelCrop.width * scaleX,
    pixelCrop.height * scaleY,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
}

const ManageVenueImages = () => {
  useEffect(() => { document.title = "Manage Images | Piatto"; }, []);
  const { id } = useParams();
  const history = useHistory();

  // Venue data
  const [venueName, setVenueName] = useState('Your Venue');
  const [googlePhotoRefs, setGooglePhotoRefs] = useState([]);
  const [existingImageUrls, setExistingImageUrls] = useState([]); // resolved Storage URLs

  // Section 2 — Google photo selection
  const [selectedRefs, setSelectedRefs] = useState([]);

  // Section 3 — User uploads
  // Each entry: { blob, previewUrl }
  const [pendingUploads, setPendingUploads] = useState([]);

  // Crop modal
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null); // data URL of file being cropped
  const [crop, setCrop] = useState({ unit: '%', width: 90, aspect: 3 / 2 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const cropImgRef = useRef(null);
  const fileInputRef = useRef(null);

  // UI
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const totalSelected = selectedRefs.length + pendingUploads.length;

  // ── Load venue doc ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const load = async () => {
      try {
        const snap = await firebase.firestore().collection('Business').doc(id).get();
        if (!snap.exists || cancelled) return;

        const data = snap.data();
        setVenueName(data.bizName || 'Your Venue');
        setGooglePhotoRefs(Array.isArray(data.googlePhotoRefs) ? data.googlePhotoRefs : []);

        // Resolve Firebase Storage paths → download URLs
        const paths = (data.images || []).filter(Boolean);
        const urls = await Promise.all(
          paths.map(async (p) => {
            try {
              return await firebase.storage().ref(p).getDownloadURL();
            } catch {
              return null;
            }
          })
        );
        if (!cancelled) setExistingImageUrls(urls.filter(Boolean));
      } catch (err) {
        console.error('Error loading venue:', err);
        showToast('Failed to load venue data', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [id]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const showToast = (message, severity = 'success') =>
    setToast({ open: true, message, severity });

  const toggleRef = (ref) => {
    setSelectedRefs((prev) => {
      if (prev.includes(ref)) return prev.filter((r) => r !== ref);
      if (prev.length + pendingUploads.length >= MAX_IMAGES) {
        showToast(`You can select at most ${MAX_IMAGES} photos in total`, 'warning');
        return prev;
      }
      return [...prev, ref];
    });
  };

  const removeUpload = (index) => {
    setPendingUploads((prev) => {
      const next = [...prev];
      URL.revokeObjectURL(next[index].previewUrl);
      next.splice(index, 1);
      return next;
    });
  };

  // ── File input → crop modal ───────────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // reset so same file can be re-selected

    if (file.size > MAX_FILE_BYTES) {
      showToast('File is larger than 10 MB — please choose a smaller image', 'warning');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result);
      setCrop({ unit: '%', width: 90, aspect: 3 / 2 });
      setCompletedCrop(null);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = useCallback((c) => setCompletedCrop(c), []);

  const handleApplyCrop = async () => {
    if (!completedCrop || !cropImgRef.current) return;

    const blob = await getCroppedBlob(cropImgRef.current, completedCrop);
    if (!blob) return;

    const previewUrl = URL.createObjectURL(blob);
    setPendingUploads((prev) => [...prev, { blob, previewUrl }]);
    setCropModalOpen(false);
    setCropSrc(null);
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (totalSelected === 0) {
      showToast('Please select or upload at least one photo', 'warning');
      return;
    }

    setSaving(true);
    try {
      // Convert pending upload blobs → base64
      const uploadedImages = await Promise.all(
        pendingUploads.map(
          ({ blob }) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const dataUrl = reader.result;
                // Strip the data URL prefix "data:image/jpeg;base64,"
                const base64 = dataUrl.split(',')[1];
                resolve({ data: base64, mimeType: 'image/jpeg' });
              };
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            })
        )
      );

      const fn = firebase
        .app()
        .functions('australia-southeast1')
        .httpsCallable('saveVenueImages');

      await fn({ businessId: id, selectedPhotoRefs: selectedRefs, uploadedImages });

      showToast('Photos saved successfully!', 'success');
      // Navigate back after a short delay so the toast is visible
      setTimeout(() => history.push(`/editBusiness/${id}`), 1500);
    } catch (err) {
      console.error('Error saving images:', err);
      showToast(err.message || 'Failed to save photos', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" role="status" aria-live="polite">
        <CircularProgress aria-label="Loading" />
      </Box>
    );
  }

  return (
    <Container component="main" maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => history.push(`/editBusiness/${id}`)}
          sx={{ mb: 2 }}
        >
          Back to Edit Business
        </Button>
        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
          Manage Photos
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {venueName} &mdash; select up to {MAX_IMAGES} photos for your listing
        </Typography>
      </Box>

      {/* Section 1 — Current saved images */}
      {existingImageUrls.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Current Photos
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These photos are currently showing on your listing. Saving new photos below will replace them.
          </Typography>
          <Grid container spacing={2}>
            {existingImageUrls.map((url, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 1,
                    overflow: 'hidden',
                    aspectRatio: '3/2',
                    bgcolor: 'grey.100',
                  }}
                >
                  <img
                    src={url}
                    alt={`Current photo ${i + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ mt: 4 }} />
        </Box>
      )}

      {/* Section 2 — Google Places photos */}
      {googlePhotoRefs.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Google Photos
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These photos were sourced from Google. Select up to {MAX_IMAGES} to add to your listing.
          </Typography>
          <Grid container spacing={2}>
            {googlePhotoRefs.map((ref, i) => {
              const proxyUrl = `${PROXY_BASE}?ref=${encodeURIComponent(ref)}`;
              const isSelected = selectedRefs.includes(ref);
              return (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Card
                    sx={{
                      position: 'relative',
                      borderRadius: 1,
                      outline: isSelected ? '3px solid' : '2px solid transparent',
                      outlineColor: isSelected ? 'primary.main' : 'transparent',
                      cursor: 'pointer',
                    }}
                    onClick={() => toggleRef(ref)}
                  >
                    <CardActionArea>
                      <CardMedia
                        component="img"
                        image={proxyUrl}
                        alt={`Google photo ${i + 1}`}
                        sx={{ aspectRatio: '3/2', objectFit: 'cover' }}
                      />
                    </CardActionArea>
                    {isSelected && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'primary.main',
                          borderRadius: '50%',
                          p: 0.25,
                          display: 'flex',
                        }}
                      >
                        <CheckCircle sx={{ color: 'white', fontSize: 20 }} />
                      </Box>
                    )}
                    <Box sx={{ position: 'absolute', top: 4, left: 4 }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleRef(ref)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          color: 'white',
                          '&.Mui-checked': { color: 'primary.main' },
                          bgcolor: 'rgba(0,0,0,0.4)',
                          borderRadius: 1,
                          p: 0.5,
                        }}
                        inputProps={{ 'aria-label': `Select Google photo ${i + 1}` }}
                      />
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          <Divider sx={{ mt: 4 }} />
        </Box>
      )}

      {/* Section 3 — Upload your own photos */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Upload Your Own Photos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload photos in any aspect ratio — we&apos;ll let you crop them to 3:2 before saving.
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {pendingUploads.map(({ previewUrl }, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 1,
                  overflow: 'hidden',
                  aspectRatio: '3/2',
                  bgcolor: 'grey.100',
                }}
              >
                <img
                  src={previewUrl}
                  alt={`Upload preview ${i + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <IconButton
                  aria-label={`Remove upload ${i + 1}`}
                  size="small"
                  onClick={() => removeUpload(i)}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' },
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            </Grid>
          ))}

          {totalSelected < MAX_IMAGES && (
            <Grid item xs={12} sm={6} md={4}>
              <Box
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                aria-label="Add photo"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  aspectRatio: '3/2',
                  border: '2px dashed',
                  borderColor: 'grey.400',
                  borderRadius: 1,
                  cursor: 'pointer',
                  bgcolor: 'grey.50',
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <Add sx={{ fontSize: 40, color: 'grey.500', mb: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                  Add photo
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Box>

      {/* Save bar */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 16,
          zIndex: 10,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          bgcolor: 'background.paper',
          p: 2,
          borderRadius: 2,
          boxShadow: 4,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', mr: 'auto' }}>
          {totalSelected} / {MAX_IMAGES} photo{totalSelected !== 1 ? 's' : ''} selected
        </Typography>
        <Button
          variant="outlined"
          onClick={() => history.push(`/editBusiness/${id}`)}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
          onClick={handleSave}
          disabled={saving || totalSelected === 0}
        >
          {saving ? 'Saving…' : 'Save Photos'}
        </Button>
      </Box>

      {/* Crop modal */}
      <Dialog
        open={cropModalOpen}
        onClose={() => setCropModalOpen(false)}
        maxWidth="md"
        fullWidth
        aria-labelledby="crop-dialog-title"
      >
        <DialogTitle id="crop-dialog-title">
          Crop Photo
          <IconButton
            aria-label="Close crop dialog"
            onClick={() => setCropModalOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Drag to adjust the crop area. The 3:2 ratio is locked to match your listing.
          </Typography>
          {cropSrc && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <ReactCrop
                src={cropSrc}
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={handleCropComplete}
                ruleOfThirds
              >
                <img
                  ref={cropImgRef}
                  src={cropSrc}
                  alt="Crop preview"
                  style={{ maxHeight: '60vh', maxWidth: '100%' }}
                />
              </ReactCrop>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCropModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleApplyCrop}
            disabled={!completedCrop?.width || !completedCrop?.height}
            startIcon={<PhotoCamera />}
          >
            Crop &amp; Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ManageVenueImages;
